import React from 'react';
import { Award, ExternalLink, TrendingUp, Users, Target, Mail } from 'lucide-react';
import Button from '../components/Button';
import Publication from '../components/Publication';
import TeamMember from '../components/TeamMember';
import { useTranslations } from '../context/TranslationContext';

const AziendaPage: React.FC = () => {
  const { t } = useTranslations();
  const values = [
    { title: t('azienda_value_1_title'), description: t('azienda_value_1_description') },
    { title: t('azienda_value_2_title'), description: t('azienda_value_2_description') },
    { title: t('azienda_value_3_title'), description: t('azienda_value_3_description') },
    { title: t('azienda_value_4_title'), description: t('azienda_value_4_description') },
  ];

  return (
    <div className="font-sans antialiased overflow-x-hidden text-text-primary bg-void-blue selection:bg-cosmic-teal selection:text-white">

      {/* Background Glow Effects - Global */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-mystic-violet/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-nebula-pink/10 rounded-full blur-[120px]" />
        <div className="absolute top-[40%] left-[20%] w-[30%] h-[30%] bg-cosmic-teal/10 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section id="hero" className="relative flex items-center pt-32 pb-16 md:pt-40 md:pb-24 z-10">
        {/* Graph Animation Background for Hero */}
        <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
          <GraphAnimation />
        </div>

        <div className="container mx-auto px-6 md:px-12 z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-1.5 rounded-full glass-panel border border-white/10">
              <span className="text-sm font-semibold text-gradient-ai uppercase tracking-wider">{t('azienda')}</span>
            </div>
            <h1 className="font-display text-xl md:text-xl lg:text-2xl font-bold mb-8 leading-tight text-white drop-shadow-2xl">
              {t('subtitle_company_section')}
            </h1>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="relative border-t border-white/10 py-16 md:py-24 z-10">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:gap-10">
            {/* Title and Description - Left */}
            <div className="lg:w-1/3 flex-shrink-0 mb-12 lg:mb-0">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                {t('azienda_mission_title')}
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                {t('azienda_mission_description')}
              </p>
            </div>

            {/* Cards - Right */}
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Mission Pillar 1 */}
                <div className="glass-panel p-8 rounded-2xl border border-white/5 hover:border-cosmic-teal/30 transition-all duration-300 hover:shadow-glow-teal group">
                  <div className="bg-cosmic-teal/10 text-cosmic-teal w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Target className="w-7 h-7" />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-3 text-white group-hover:text-cosmic-teal transition-colors">
                    {t('azienda_reliability_title')}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {t('azienda_reliability_description')}
                  </p>
                </div>

                {/* Mission Pillar 2 */}
                <div className="glass-panel p-8 rounded-2xl border border-white/5 hover:border-mystic-violet/30 transition-all duration-300 hover:shadow-glow-violet group">
                  <div className="bg-mystic-violet/10 text-mystic-violet w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-7 h-7" />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-3 text-white group-hover:text-mystic-violet transition-colors">
                    {t('azienda_innovation_title')}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {t('azienda_innovation_description')}
                  </p>
                </div>

                {/* Mission Pillar 3 */}
                <div className="glass-panel p-8 rounded-2xl border border-white/5 hover:border-nebula-pink/30 transition-all duration-300 hover:shadow-glow-pink group">
                  <div className="bg-nebula-pink/10 text-nebula-pink w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-7 h-7" />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-3 text-white group-hover:text-nebula-pink transition-colors">
                    {t('azienda_accessibility_title')}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {t('azienda_accessibility_description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Section */}
      <section id="origin" className="relative border-t border-white/10 py-16 md:py-24 z-10">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:gap-10">
            {/* Title and Description - Left */}
            <div className="lg:w-1/3 flex-shrink-0 mb-12 lg:mb-0">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                {t('azienda_origin_title')}
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                {t('azienda_origin_description')}
              </p>
            </div>

            {/* Cards - Right */}
            <div className="flex-1">
              <div className="glass-panel p-10 rounded-2xl border border-white/5 hover:border-starlight-gold/30 transition-all duration-300 hover:shadow-glow-gold group relative overflow-hidden">
                <div className="absolute top-0 right-0 p-32 bg-starlight-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-starlight-gold/10 transition-colors duration-500"></div>

                <img
                  src="/images/spinoffPoli2.jpg"
                  alt={t('polimi_spinoff_alt')}
                  className="w-full max-w-md h-auto mb-8 opacity-90 mix-blend-screen mx-auto block"
                  onError={(e) => { e.currentTarget.src = '/images/spinoffPoli2.jpg'; }}
                />
                <h3 className="text-2xl font-bold text-white mb-4 leading-snug">
                  {t('azienda_polimi_spinoff_title')}
                </h3>
                <p className="text-white/80 text-lg leading-relaxed mb-8">{t('subtitle_spinoff_polimi')}</p>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open('https://www.polimi.it/ricerca/trasferimento-tecnologico/spin-off', '_blank', 'noopener,noreferrer');
                  }}
                  className="inline-flex w-full items-center justify-between rounded-xl border border-white/10 bg-gradient-to-r from-cosmic-teal/20 to-transparent px-4 py-3 text-sm font-bold text-white transition-all duration-300 hover:border-white/20 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cosmic-teal focus:ring-offset-2 focus:ring-offset-void-blue"
                >
                  {t("learn_more")}
                  <ExternalLink size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Recognition Section */}
      <section id="research" className="relative border-t border-white/10 py-16 md:py-24 z-10">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:gap-10">
            {/* Title and Description - Left */}
            <div className="lg:w-1/3 flex-shrink-0 mb-12 lg:mb-0">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                {t('azienda_awarded_research_title')}
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                {t('azienda_awarded_research_description')}
              </p>
            </div>

            {/* Content - Right */}
            <div className="flex-1">
              <div className="glass-panel p-10 rounded-2xl border border-white/5 hover:border-cosmic-teal/30 transition-all duration-300 hover:shadow-glow-teal group">
                <div className="flex flex-col md:flex-row items-start gap-8 mb-8">
                  {/* Left side - Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex-shrink-0 p-3 bg-cosmic-teal/10 rounded-xl">
                        <Award className="w-8 h-8 text-cosmic-teal" />
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {t('azienda_chamber_award_title')}
                      </h3>
                    </div>
                    <p className="text-white/80 text-lg leading-relaxed mb-6">
                      {t('azienda_chamber_award_description')}
                    </p>
                    <button
                      onClick={() => window.open('https://comunicazione.camera.it/archivio-prima-pagina/19-42168', '_blank', 'noopener,noreferrer')}
                      className="inline-flex w-full items-center justify-between rounded-xl border border-white/10 bg-gradient-to-r from-cosmic-teal/20 to-transparent px-4 py-3 text-sm font-bold text-white transition-all duration-300 hover:border-white/20 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cosmic-teal focus:ring-offset-2 focus:ring-offset-void-blue"
                    >
                      {t('view_article')}
                      <ExternalLink size={16} aria-hidden="true" />
                    </button>
                  </div>

                  {/* Right side - Image */}
                  <div className="w-full md:w-auto md:flex-shrink-0">
                    <img
                      src="/images/premioParlamento.jpg"
                      alt={t('awarded_research_alt')}
                      className="w-full md:w-48 h-48 rounded-xl border border-white/10 object-cover shadow-lg"
                      onError={(e) => { e.currentTarget.src = '/images/premioParlamento.jpg'; }}
                    />
                  </div>
                </div>

                <div className="bg-black/30 rounded-xl p-6 border border-white/5">
                  <p className="text-white/80 text-lg leading-relaxed italic">
                    "{t('azienda_chamber_award_quote')}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section id="publications" className="relative border-t border-white/10 py-16 md:py-24 z-10">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:gap-10 mb-10">
            <div className="lg:w-1/3 flex-shrink-0 mb-8 lg:mb-0">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
                {t('our_scientific_publications')}
              </h2>
            </div>
            <div className="flex-1">
              <p className="text-white/80 text-xl leading-relaxed">
                {t('publications_intro')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* New Publication 1 */}
            <Publication
              year="2025"
              title="LegisSearch: navigating legislation with graphs and large language models"
              journal="Artificial Intelligence and Law"
              description={t('publication_1_description')}
              link=""
            />
            {/* New Publication 2 */}
            <Publication
              year="2025"
              title="LLM-assisted Construction of the United States Legislative Graph"
              journal="Proceedings of the VLDB Endowment"
              description={t('publication_2_description')}
              link=""
            />
            <Publication
              year="2025"
              title="An LLM-assisted ETL pipeline to build a high-quality knowledge graph of the Italian legislation."
              journal="In: Information Processing and Management"
              description={t('publication_3_description')}
              link="https://www.sciencedirect.com/science/article/pii/S030645732500024X"
            />
            <Publication
              year="2025"
              title="Legislative Knowledge Management with Property Graphs"
              journal="In: Proceedings of the EDBT25 Workshops"
              description={t('publication_4_description')}
              link="https://ceur-ws.org/Vol-3946/TGD-1.pdf"
            />
            <Publication
              year="2024"
              title="Leveraging Knowledge Graphs and LLMs to Support and Monitor Legislative Systems"
              journal="In: Proceedings of the 33rd CIKM Conference"
              description={t('publication_5_description')}
              link="https://dl.acm.org/doi/10.1145/3627673.3680268"
            />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="values" className="relative border-t border-white/10 py-16 md:py-24 z-10">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:gap-10 mb-10">
            {/* Title - Left */}
            <div className="lg:w-1/3 flex-shrink-0 mb-8 lg:mb-0">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
                {t('azienda_values_title')}
              </h2>
            </div>

            {/* Values List - Right */}
            <div className="flex-1">
              <div className="space-y-6">
                {values.map((value, index) => (
                  <div key={value.title} className="glass-panel p-8 rounded-2xl border border-white/5 hover:border-white/20 transition-all duration-300 hover:translate-x-2">
                    <div className="flex items-start gap-6">
                      <div className="w-10 flex-shrink-0 pt-0.5 text-3xl font-bold leading-none text-cosmic-teal" aria-hidden="true">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">
                          {value.title}
                        </h3>
                        <p className="text-white/80 text-lg leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team & Advisors Section */}
      <section id="team" className="relative border-t border-white/10 py-16 md:py-24 z-10">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
              {t('azienda_team_advisors_title')}
            </h2>
          </div>

          {/* Team Members */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60 mb-10 text-center">
              {t('azienda_our_team_title')}
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              <TeamMember imagePath="/images/people/andrea.jpeg" name="Andrea Colombo" position={t('role_ceo')} />
              <TeamMember imagePath="/images/people/francesco.jpeg" name="Francesco Invernici" position={t('role_cto')} />
              <TeamMember imagePath="/images/people/chiara.jpeg" name="Chiara Leonardi" position={t('role_cdo')} />
              <TeamMember imagePath="/images/people/claudio.jpeg" name="Claudio Michelacci" position={t('role_cro')} />
            </div>
          </div>

          {/* Advisors */}
          <div>
            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60 mb-10 text-center">
              {t('azienda_our_advisors_title')}
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
              <TeamMember imagePath="/images/people/anna.jpeg" name="Anna Bernasconi" position={t('role_scientific_advisor')} />
              <TeamMember imagePath="/images/people/stefano.jpeg" name="Stefano Ceri" position={t('role_scientific_advisor')} />
              <TeamMember imagePath="/images/people/luigi.jpeg" name="Luigi Guiso" position={t('role_scientific_advisor')} />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative border-t border-white/10 py-16 md:py-24 z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-void-blue to-deep-space z-0"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl bg-gradient-radial from-cosmic-teal/10 to-transparent opacity-50 blur-3xl pointer-events-none"></div>

        <div className="container mx-auto px-6 md:px-12 text-center max-w-4xl relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-8 text-white">
            {t('solutions_cta_title')}
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-white/80">
            {t('solutions_cta_subtitle')}
          </p>
          <p className="text-lg mb-12 text-white/80">
            {t('solutions_cta_description')}
          </p>

          <Button
            size="lg"
            variant="gradient"
            icon={<Mail size={20} />}
            onClick={() => {
              const subject = encodeURIComponent(t("demo_email_subject"));
              const body = encodeURIComponent(t("demo_email_body"));
              window.location.href = `mailto:info@legisratio.it?subject=${subject}&body=${body}`;
            }}
            className="w-full sm:w-auto shadow-glow-teal hover:shadow-glow-teal-lg transition-shadow duration-300"
          >
            {t('solutions_book_demo')}
          </Button>
        </div>
      </section>
    </div>
  );
};

// Inline Graph Animation Component
const GraphAnimation: React.FC = () => {
  const svgRef = React.useRef<SVGSVGElement>(null);

  React.useEffect(() => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const width = svg.clientWidth;
    const height = svg.clientHeight;
    const padding = 30; // Padding to keep nodes visible

    // Detect mobile
    const isMobile = window.innerWidth < 768;

    // Adjust parameters based on screen size
    const nodeCount = isMobile ? 20 : 40; // Even more nodes
    const baseSpeed = isMobile ? 0.15 : 0.3;
    const minDistance = isMobile ? 60 : 70;

    // Create animated graph nodes with slower movement and bigger sizes
    const nodes: Array<{ x: number; y: number; vx: number; vy: number; radius: number }> = [];

    // Function to check if position is valid (far enough from other nodes)
    const isValidPosition = (x: number, y: number, existingNodes: typeof nodes): boolean => {
      return !existingNodes.some(other => {
        const dx = other.x - x;
        const dy = other.y - y;
        return Math.sqrt(dx * dx + dy * dy) < minDistance;
      });
    };

    for (let i = 0; i < nodeCount; i++) {
      let x: number = 0;
      let y: number = 0;
      let attempts = 0;
      const maxAttempts = 50;

      // Try to place node away from others
      do {
        x = padding + Math.random() * (width - 2 * padding);
        y = padding + Math.random() * (height - 2 * padding);
        attempts++;
      } while (attempts < maxAttempts && !isValidPosition(x, y, nodes));

      nodes.push({
        x,
        y,
        vx: (Math.random() - 0.5) * baseSpeed,
        vy: (Math.random() - 0.5) * baseSpeed,
        radius: Math.random() * 6 + 3 // Larger nodes for better visibility
      });
    }

    let animationId: number;

    const animate = () => {
      // Clear previous frame
      while (svg.firstChild) {
        svg.removeChild(svg.firstChild);
      }

      // Update positions
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges with padding
        if (node.x - node.radius < padding || node.x + node.radius > width - padding) node.vx *= -1;
        if (node.y - node.radius < padding || node.y + node.radius > height - padding) node.vy *= -1;

        // Keep within bounds
        node.x = Math.max(padding + node.radius, Math.min(width - padding - node.radius, node.x));
        node.y = Math.max(padding + node.radius, Math.min(height - padding - node.radius, node.y));
      });

      // Apply repulsion force to prevent clustering
      nodes.forEach((node, i) => {
        nodes.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // If nodes are too close, push them apart
          if (dist < minDistance && dist > 0) {
            const force = (minDistance - dist) / minDistance * 0.5;
            const angle = Math.atan2(dy, dx);
            node.vx -= Math.cos(angle) * force * 0.1;
            node.vy -= Math.sin(angle) * force * 0.1;
            other.vx += Math.cos(angle) * force * 0.1;
            other.vy += Math.sin(angle) * force * 0.1;
          }
        });
      });

      // Create gradient defs
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svg.appendChild(defs);

      // Draw links with neon color
      nodes.forEach((node, i) => {
        nodes.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 220) { // Even longer connection distance
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', node.x.toString());
            line.setAttribute('y1', node.y.toString());
            line.setAttribute('x2', other.x.toString());
            line.setAttribute('y2', other.y.toString());
            line.setAttribute('stroke', '#8B5CF6'); // Mystic Violet
            line.setAttribute('stroke-opacity', (0.5 * (1 - dist / 220)).toString()); // Increased opacity
            line.setAttribute('stroke-width', '1.5'); // Thicker lines
            svg.appendChild(line);
          }
        });
      });

      // Draw nodes as glowing dots
      nodes.forEach(node => {
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', node.x.toString());
        circle.setAttribute('cy', node.y.toString());
        circle.setAttribute('r', node.radius.toString());
        circle.setAttribute('fill', '#2DD4BF'); // Cosmic Teal
        circle.setAttribute('fill-opacity', '0.8');
        svg.appendChild(circle);
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <svg ref={svgRef} className="w-full h-full" style={{
      background: 'transparent'
    }} />
  );
};

export default AziendaPage;
